import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { Connector, ConnectionsService } from '../../services/connections.service';
import { BrandMarkComponent } from '../brand-mark/brand-mark.component';

type ConnFilter = 'all' | 'connected' | 'off';

/** Where the user can withdraw the grant inside the provider itself.
 *  Disconnecting here stops AGENTS using the connection (the platform
 *  enforces it at the gateway within seconds), but no vendor in this catalog
 *  offers an app-initiated token revoke — the grant recorded on the vendor
 *  side only disappears when the user removes it in the vendor's own
 *  connected-apps screen. Only vendors with a stable, org-independent URL
 *  are listed; the rest fall back to prose without a link. */
const PROVIDER_REVOKE_URLS: Record<string, string> = {
  atlassian: 'https://id.atlassian.com/manage-profile/apps',
  // Rovo MCP DCR grants surface on the same connected-apps screen (the
  // MCP scoped-token artifact lives under the user's Atlassian profile).
  'atlassian-mcp': 'https://id.atlassian.com/manage-profile/apps',
  // "Apps and services you can use" ≠ consent revocation: myapps.microsoft.com
  // is the enterprise-app launcher and exposes nothing to revoke. The page
  // that actually lists "apps and services you've given access to" is
  // myaccount.microsoft.com/appconsent.
  microsoft: 'https://myaccount.microsoft.com/appconsent',
  'microsoft-meetings': 'https://myaccount.microsoft.com/appconsent',
  github: 'https://github.com/settings/applications',
};

/** OAuth scope → plain language. Unknown scopes render verbatim in mono —
 *  never hide what the user is being asked to grant. */
const SCOPE_LABELS: Record<string, string> = {
  'read:jira-work': 'Read Jira issues you can see',
  'read:jira-user': 'Look up Jira users',
  'read:confluence-content.all': 'Read Confluence pages you can see',
  'offline_access': 'Stay connected between visits',
  'api': 'Access Salesforce data as you',
  'refresh_token': 'Stay connected between visits',
  'User.Read': 'Read your own profile',
  'Calendars.Read': 'Read your calendar',
  'Calendars.ReadWrite': 'Read and update your calendar',
  'OnlineMeetings.Read': 'Read meetings you organise',
  'OnlineMeetingTranscript.Read.All': 'Read transcripts of your meetings',
};

/**
 * Connections — per-user OAuth connectors, as a modal over whatever surface
 * the user is on.
 *
 * Two panes on one sliding track: a compact list of every connector, and a
 * detail pane for one of them (scopes in plain language, when it was linked,
 * connect/disconnect). The footer is a single row that swaps by context — the
 * vault promise on the list, the consent CTA on an unconnected connector, the
 * disconnect confirm inline — so there is never a second stacked dialog.
 *
 * Mounted once in the layout shell and opened through
 * ConnectionsService.triggerOpen(), like the Abilities and Agent Catalog
 * modals. The /connections route still exists purely as the OAuth return
 * target (see ConnectionsEntryComponent) — it hands its `session_id` here so
 * the grant can be finalized, then bounces back to chat.
 *
 * Tokens never reach this component. It sees status metadata and a single-use
 * consent URL it hands straight to the browser.
 */
@Component({
  selector: 'app-connections-modal',
  standalone: true,
  imports: [BrandMarkComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './connections-modal.component.html',
  styleUrls: ['./connections-modal.component.scss'],
})
export class ConnectionsModalComponent implements OnInit, OnDestroy {
  private readonly connectionsSvc = inject(ConnectionsService);

  /** Visibility lives on the service so the rails can light up their
   *  Connections entry while this is on screen. */
  readonly open = this.connectionsSvc.modalOpen;
  readonly connectors = signal<Connector[]>([]);
  readonly loading = signal(false);
  readonly error = signal(false);
  /** Re-verify in flight (header refresh) — distinct from the first load, so
   *  the list stays on screen and only the icon spins. */
  readonly verifying = signal(false);

  /** Connector with an in-flight authorize/disconnect. */
  readonly busy = signal<string | null>(null);
  /** Disconnect is one click from confirmed (detail pane only). */
  readonly confirming = signal(false);
  readonly filter = signal<ConnFilter>('all');

  /** Which connector the detail pane renders. Kept after `back()` so the pane
   *  stays painted while the track slides away — clearing it would blank the
   *  detail mid-animation. */
  readonly detailName = signal<string | null>(null);
  /** Whether the track is slid over to the detail pane. */
  readonly showDetail = signal(false);

  readonly toast = signal<string | null>(null);

  // ── BYO (user_api_key) credential form ──
  // The paste form is deliberately reachable ONLY from buttons on this modal —
  // no route, no deep link, no chat-card shortcut — so a phishing mail can
  // never link someone straight to a token prompt.

  /** Whether the token form overlay is on screen. */
  readonly credOpen = signal(false);
  /** Connector the form is for (looked up live so a refresh updates it). */
  readonly credName = signal<string | null>(null);
  /** True when the form was opened from a connected card ("Rotate token"). */
  readonly credRotating = signal(false);
  readonly credEmail = signal('');
  /** The pasted secret. Lives only in this signal + the masked input; cleared
   *  the moment the form closes and NEVER rendered anywhere as text. */
  readonly credToken = signal('');
  readonly credSaving = signal(false);
  readonly credError = signal<string | null>(null);

  private readonly destroying$ = new Subject<void>();
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  /** Unfinalized post-consent session. Sourced from the entry route (or its
   *  sessionStorage stash, which survives /login bounces and failed
   *  navigations) and kept until a load that carried it SUCCEEDS — so a
   *  transient finalize failure can be retried instead of silently discarding
   *  the user's successful consent. */
  private pendingSessionUri: string | null = null;

  ngOnInit(): void {
    this.connectionsSvc.open$.pipe(takeUntil(this.destroying$)).subscribe(sessionUri => {
      this.filter.set('all');
      this.showDetail.set(false);
      this.confirming.set(false);
      this.detailName.set(null);
      this.open.set(true);
      // A pending consent can arrive directly (post-consent entry route) or
      // from the stash (the entry ran but the finalize never completed —
      // login bounce, network blip). Either way it rides every load until
      // one succeeds.
      this.pendingSessionUri = sessionUri ?? this.connectionsSvc.peekPendingSession();
      // verify=true on every open: reconcile stored status against the token
      // vault. sessionUri (post-consent only) additionally finalizes the grant.
      this.load(true);
      setTimeout(() => document.querySelector<HTMLElement>('.cx-modal')?.focus(), 60);
    });
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  // ── data ──

  load(verify = false): void {
    const first = this.connectors().length === 0;
    if (first) this.loading.set(true); else this.verifying.set(true);
    this.error.set(false);
    // Any unfinalized consent rides along until a load succeeds — including
    // the error pane's Retry, which previously dropped it, permanently
    // discarding a successful consent on one transient 5xx.
    const sessionUri = this.pendingSessionUri;
    this.connectionsSvc.list(verify, sessionUri).subscribe({
      next: list => {
        if (sessionUri) {
          this.pendingSessionUri = null;
          this.connectionsSvc.clearPendingSession();
        }
        this.connectors.set(list);
        this.loading.set(false);
        this.verifying.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.verifying.set(false);
        this.error.set(true);
      },
    });
  }

  refresh(): void {
    if (this.verifying() || this.loading()) return;
    this.load(true);
  }

  // ── derived ──

  isReady(c: Connector): boolean { return c.connectorStatus === 'active'; }
  isConnected(c: Connector): boolean { return c.connection?.status === 'active'; }
  /** BYO connector: the user pastes a vendor API token instead of an OAuth
   *  consent round-trip. Everything else renders the unchanged OAuth card. */
  isByo(c: Connector): boolean { return c.credentialKind === 'user_api_key'; }
  /** A grant exists but is no longer usable (revoked / disconnected / stuck
   *  pending) — the user has to re-consent. */
  isRevoked(c: Connector): boolean { return !!c.connection && c.connection.status !== 'active'; }
  isSoon(c: Connector): boolean { return !this.isReady(c); }

  readonly ready = computed(() => this.connectors().filter(c => this.isReady(c)));
  readonly connectedCount = computed(() => this.ready().filter(c => this.isConnected(c)).length);
  readonly notConnectedCount = computed(() => this.ready().length - this.connectedCount());
  readonly needsAttention = computed(() => this.ready().some(c => this.isRevoked(c)));

  /** Rows for the current filter. Anything needing the user's attention floats
   *  to the top, then live connections, then the rest, then coming-soon. */
  readonly rows = computed<Connector[]>(() => {
    const f = this.filter();
    let list = this.connectors();
    if (f === 'connected') list = this.ready().filter(c => this.isConnected(c));
    else if (f === 'off') list = this.ready().filter(c => !this.isConnected(c));
    const rank = (c: Connector) =>
      this.isReady(c) && this.isRevoked(c) ? 0 : this.isConnected(c) ? 1 : this.isReady(c) ? 2 : 3;
    return [...list].sort((a, b) => rank(a) - rank(b) || a.displayName.localeCompare(b.displayName));
  });

  readonly detail = computed<Connector | null>(() => {
    const name = this.detailName();
    return name ? this.connectors().find(c => c.name === name) ?? null : null;
  });

  readonly credConnector = computed<Connector | null>(() => {
    const name = this.credName();
    return name ? this.connectors().find(c => c.name === name) ?? null : null;
  });

  /** Only the email:api_token format (e.g. Atlassian) needs the account-email
   *  field — a bare-token connector (e.g. Catsy) has nothing to put there. */
  readonly credNeedsEmail = computed(() => this.credConnector()?.credentialFormat === 'email:api_token');

  readonly canSaveCred = computed(() =>
    !this.credSaving()
    && (!this.credNeedsEmail() || this.credEmail().trim().length > 0)
    && this.credToken().trim().length > 0);

  readonly empty = computed(() =>
    !this.loading() && !this.error() && this.connectors().length === 0);

  statusLabel(c: Connector): string {
    if (this.isConnected(c)) return 'Connected';
    if (this.isSoon(c)) return 'Coming soon';
    return this.isRevoked(c) ? 'Reconnect needed' : 'Not connected';
  }

  /** Status class suffix — drives the dot/pill colour. */
  statusKind(c: Connector): 'on' | 'warn' | 'off' {
    if (this.isConnected(c)) return 'on';
    return this.isReady(c) && this.isRevoked(c) ? 'warn' : 'off';
  }

  scopeLabel(scope: string): string { return SCOPE_LABELS[scope] || scope; }
  isKnownScope(scope: string): boolean { return scope in SCOPE_LABELS; }

  /** What the permissions list shows. Registry-supplied capability lines win
   *  over raw scopes: a vendor MCP grant is a vendor-defined bundle, so its
   *  scope list describes plumbing, not permissions — Atlassian MCP's only
   *  scope is `offline_access`, which rendered as the lone (and misleading)
   *  line "Stay connected between visits" under "what agents can do as you". */
  permissionLines(c: Connector): string[] {
    return c.capabilities?.length ? c.capabilities : (c.scopes ?? []).map(s => this.scopeLabel(s));
  }

  /** Capability lines are authored prose — never render them as raw-scope mono. */
  isRawLine(c: Connector, line: string): boolean {
    return !c.capabilities?.length && !this.isKnownScope(
      (c.scopes ?? []).find(s => this.scopeLabel(s) === line) ?? line);
  }

  connectLabel(c: Connector): string { return this.isRevoked(c) ? 'Reconnect' : 'Connect'; }

  providerRevokeUrl(c: Connector): string | null {
    return PROVIDER_REVOKE_URLS[c.name] ?? null;
  }

  /** Post-disconnect only: the user just severed the platform side, so tell
   *  them the one thing the platform cannot do for them. */
  showProviderRevoke(c: Connector): boolean {
    return this.isReady(c) && c.connection?.status === 'revoked';
  }

  /** Heading over the scope list — the same permissions read differently
   *  before and after you've granted them. */
  scopeTitle(c: Connector): string {
    if (this.isConnected(c)) return 'WHAT AGENTS CAN DO AS YOU';
    if (this.isSoon(c)) return 'PLANNED PERMISSIONS';
    return 'WHAT YOU WOULD BE GRANTING';
  }

  // ── navigation within the modal ──

  select(c: Connector): void {
    this.detailName.set(c.name);
    this.confirming.set(false);
    this.showDetail.set(true);
  }

  back(): void {
    this.showDetail.set(false);
    this.confirming.set(false);
  }

  close(): void {
    this.open.set(false);
    this.showDetail.set(false);
    this.confirming.set(false);
    this.detailName.set(null);
    // An in-flight authorize/disconnect must not survive the modal: a stuck
    // `busy` would disable Connect/Disconnect for the rest of the session,
    // and connect()'s redirect guard keys off open() + busy.
    this.busy.set(null);
    // The token form (and above all the pasted secret) must not outlive the
    // modal either.
    this.credOpen.set(false);
    this.credSaving.set(false);
    this.credToken.set('');
    this.credError.set(null);
  }

  setFilter(f: ConnFilter): void {
    this.filter.set(f);
    document.querySelector('.cx-list')?.scrollTo({ top: 0 });
  }

  /** Escape unwinds one layer at a time: token form → confirm → detail → close. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.open()) return;
    if (this.credOpen()) { this.closeCred(); return; }
    if (this.confirming()) { this.confirming.set(false); return; }
    if (this.showDetail()) { this.back(); return; }
    this.close();
  }

  // ── connect / disconnect ──

  connect(c: Connector, ev?: Event): void {
    ev?.stopPropagation();
    if (this.busy()) return;
    this.busy.set(c.name);
    this.connectionsSvc.authorize(c.name).subscribe({
      next: res => {
        if (!this.open()) {
          // The user closed the modal while the authorize round-trip was in
          // flight — yanking the whole tab to a consent screen they walked
          // away from would be hostile. The minted URL is single-use and
          // session-bound; letting it lapse is safe.
          this.busy.set(null);
          return;
        }
        if (res.status === 'connected') {
          this.busy.set(null);
          this.load(true);
          this.say(`${c.displayName} connected`);
          return;
        }
        if (res.authorizationUrl) {
          // SAME-TAB navigation, deliberately not window.open. The hub keeps
          // its session in sessionStorage, which is per-tab: a popup returning
          // from the provider had no session, so the auth guard bounced the
          // consent tab to /login and the post-consent reconcile never ran.
          // Navigating in place preserves sessionStorage, so the provider
          // returns us to /connections signed in, the entry route hands the
          // session_id back here and the grant finalizes. (Single-use,
          // session-bound URL — handed to the browser, never stored.)
          window.location.assign(res.authorizationUrl);
          return;
        }
        this.busy.set(null);
        this.say(`Couldn't start the ${c.displayName} connection — try again`);
      },
      error: () => {
        this.busy.set(null);
        this.say(`Couldn't start the ${c.displayName} connection — try again`);
      },
    });
  }

  askDisconnect(): void { this.confirming.set(true); }
  cancelDisconnect(): void { this.confirming.set(false); }

  disconnect(c: Connector): void {
    if (this.busy()) return;
    this.confirming.set(false);
    this.busy.set(c.name);
    this.connectionsSvc.disconnect(c.name).subscribe({
      next: () => {
        this.busy.set(null);
        this.load(true);
        this.say(this.isByo(c)
          ? `${c.displayName} token removed — agents lose access within a minute`
          : `${c.displayName} disconnected — agents lose access within a minute`);
      },
      error: () => {
        this.busy.set(null);
        this.say(`Couldn't disconnect ${c.displayName} — try again`);
      },
    });
  }

  // ── BYO token form (user_api_key) ──

  /** Open the paste form — from an "Add token" CTA or "Rotate token". */
  openCred(c: Connector, ev?: Event): void {
    ev?.stopPropagation();
    this.credName.set(c.name);
    this.credRotating.set(this.isConnected(c));
    const needsEmail = c.credentialFormat === 'email:api_token';
    let email = '';
    if (needsEmail) {
      try { email = (sessionStorage.getItem('current-email') ?? '').trim(); } catch { /* storage blocked — leave blank */ }
    }
    this.credEmail.set(email);
    this.credToken.set('');
    this.credError.set(null);
    this.credSaving.set(false);
    this.credOpen.set(true);
    // No email field at all for a bare-token connector — always focus the
    // token input then; otherwise the usual "email pre-filled? skip to
    // token" logic.
    const focusSelector = !needsEmail || email ? '#cx-cred-token' : '#cx-cred-email';
    setTimeout(() => document.querySelector<HTMLElement>(focusSelector)?.focus(), 60);
  }

  /** Close + scrub. Blocked while the save round-trip is in flight so the
   *  outcome (success toast or inline vendor message) is never lost. */
  closeCred(): void {
    if (this.credSaving()) return;
    this.credOpen.set(false);
    this.credToken.set('');
    this.credError.set(null);
  }

  onCredEmailInput(ev: Event): void { this.credEmail.set((ev.target as HTMLInputElement).value); }
  onCredTokenInput(ev: Event): void { this.credToken.set((ev.target as HTMLInputElement).value); }

  /** Native form submit, so Enter in either field saves. */
  onCredSubmit(ev: Event): void {
    ev.preventDefault();
    this.saveCred();
  }

  saveCred(): void {
    const c = this.credConnector();
    if (!c || !this.canSaveCred()) return;
    this.credSaving.set(true);
    this.credError.set(null);
    this.connectionsSvc.putCredential(c.name, this.credToken().trim(), this.credEmail().trim()).subscribe({
      next: () => {
        this.credSaving.set(false);
        this.credOpen.set(false);
        this.credToken.set('');
        // Refresh brings back the connected row + its (new) fingerprint.
        this.load(true);
        this.say(this.credRotating() ? `${c.displayName} token updated` : `${c.displayName} connected`);
      },
      error: (err: { status?: number; message?: string }) => {
        this.credSaving.set(false);
        // Inline, form stays open: 400 = the vendor looked at it and said no
        // (surface their reason verbatim); anything else = we couldn't get an
        // answer, so the token may well be fine — invite a retry.
        if (err?.status === 400) {
          this.credError.set(err.message || `${c.displayName} rejected that token — check the email and token, then try again.`);
        } else {
          this.credError.set(`Couldn't validate the token right now — try again in a moment.`);
        }
      },
    });
  }

  private say(message: string): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toast.set(message);
    this.toastTimer = setTimeout(() => this.toast.set(null), 2800);
  }

  // ── dev-sandbox install (packaged connect app) ──
  // Rendered only when the registry publishes sandbox_install for the
  // connector. The command is the vendor's own CLI verbatim — the wizard is
  // the distribution surface, not a custom script. --apex-compile package
  // compiles only the package's Apex (it has none), so an unrelated broken
  // class in the developer's sandbox can't fail the install.

  sandboxCmd(c: Connector): string {
    const id = c.sandboxInstall?.packageVersionId ?? '';
    return `sf package install --package ${id} --target-org YOUR_SANDBOX_ALIAS --wait 15 --no-prompt --apex-compile package`;
  }

  sandboxInstallUrl(c: Connector): string {
    const id = c.sandboxInstall?.packageVersionId ?? '';
    return `https://test.salesforce.com/packaging/installPackage.apexp?p0=${id}`;
  }

  copySandboxCmd(c: Connector): void {
    navigator.clipboard?.writeText(this.sandboxCmd(c)).then(
      () => this.say('Install command copied'),
      () => this.say('Copy failed — select the command text instead'),
    );
  }

  // ── formatting (API sends epoch SECONDS) ──

  fmtDate(sec: number | null): string {
    if (!sec) return '—';
    return new Date(sec * 1000).toLocaleDateString(undefined,
      { day: '2-digit', month: 'short', year: 'numeric' });
  }

  fmtAgo(sec: number | null): string {
    if (!sec) return '—';
    const diff = Math.floor(Date.now() / 1000 - sec);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
    return this.fmtDate(sec);
  }

  stop(ev: Event): void { ev.stopPropagation(); }
}
